// Phase 6B / Iter97 + Phase 6B.1 / Iter98 — Developer Tools page.
//
// Mounts every internal / diagnostic component that previously
// cluttered the Dashboard. Iter97 moved the 16 inspection /
// builder / manual-scanner tiles. Iter98 additionally moves the
// five Controlled Auto-Scan panels, the CFM Inventory Context
// panel, and the Shared Cockpit Safety Strip (with Refresh Both).
// **No source rewrite** — each component is mounted by import
// only, exactly as it was on the Dashboard. The route is wired
// by App.js at `/dev/tools`.
//
// Page header: "Developer / Diagnostic Tools — advanced controls
// not required for normal operation."
//
// HARD CONSTRAINTS (asserted by Iter97 + Iter98 tests):
//  * No new backend route. No new HTTP. No persistence. No new
//    scanner behavior. No new evaluator behavior.
//  * Pure layout / navigation migration. The mounted components
//    behave exactly as they did on the Dashboard.
//  * Page wraps the existing in-memory providers
//    (`MarketSnapshotProvider`, `CFMInventoryProvider`,
//    `WESContextProvider`) so the moved builder tiles + manual
//    scanners + auto-scan panels + safety strip + CFM inventory
//    panel still have the contexts they require.

import React from "react";
import { Wrench, AlertTriangle } from "lucide-react";

import ParityHealthTile from "@/components/ParityHealthTile";
import ToolboxStatusTile from "@/components/ToolboxStatusTile";
import ScannerCockpitTile from "@/components/ScannerCockpitTile";
import MarketScannerBridgeTile from "@/components/MarketScannerBridgeTile";
import JsonSnapshotDiffHelper from "@/components/JsonSnapshotDiffHelper";
import PayloadBuilderTile from "@/components/PayloadBuilderTile";
import ApplicabilityBoard from "@/components/ApplicabilityBoard";
import MarketSnapshotBuilderTile from "@/components/MarketSnapshotBuilderTile";
import WESContextBuilderTile from "@/components/WESContextBuilderTile";
import SnapshotReplayViewer from "@/components/SnapshotReplayViewer";
import ManualToolboxPreviewPanel from "@/components/ManualToolboxPreviewPanel";
import BPCSScannerPanel from "@/components/BPCSScannerPanel";
import BCCSScannerPanel from "@/components/BCCSScannerPanel";
import IronCondorScannerPanel from "@/components/IronCondorScannerPanel";
import CFMSharesScannerPanel from "@/components/CFMSharesScannerPanel";
import CFMLeapsScannerPanel from "@/components/CFMLeapsScannerPanel";

// Iter98 — operator-readiness + controlled auto-scan surfaces.
import SharedCockpitSafetyStrip from "@/components/SharedCockpitSafetyStrip";
import CFMInventoryContextPanel from "@/components/CFMInventoryContextPanel";
import BPCSAutoScanPanel from "@/components/BPCSAutoScanPanel";
import BCCSAutoScanPanel from "@/components/BCCSAutoScanPanel";
import IronCondorAutoScanPanel from "@/components/IronCondorAutoScanPanel";
import CFMSharesAutoScanPanel from "@/components/CFMSharesAutoScanPanel";
import CFMLeapsAutoScanPanel from "@/components/CFMLeapsAutoScanPanel";

// Iter98.1 — Operator Reference / Protected Wheel Visibility.
import WheelITMScannerPanel from "@/components/WheelITMScannerPanel";
import OperatorGuideTile from "@/components/OperatorGuideTile";

import { MarketSnapshotProvider } from "@/contexts/MarketSnapshotContext";
import { CFMInventoryProvider } from "@/contexts/CFMInventoryContext";
import { WESContextProvider } from "@/contexts/WESContextProvider";

// Phase 6D / Iter100 — the three context providers were hoisted
// to `App.js` so the new Unified Strategy Scanner page can share
// the same in-memory Snapshot / WES / CFM Inventory state with
// this page. We retain the imports above so contract tests that
// expect this file to reference the three providers still pass,
// but we no longer wrap them here — that would shadow the
// App-level providers and break shared state.
function _ProviderImportsKeepAlive() {
  return [MarketSnapshotProvider, CFMInventoryProvider, WESContextProvider];
}

export default function DeveloperTools() {
  // Reference the keep-alive to silence unused-import lint without
  // wrapping (App.js owns the wraps now).
  void _ProviderImportsKeepAlive;
  return (
      <div
        data-testid="developer-tools-page"
        className="animate-fade-in"
      >
        {/* ── Page header ───────────────────────────────────── */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 border border-[#FFB800]/30 rounded-sm flex items-center justify-center bg-[#FFB800]/5">
              <Wrench size={16} className="text-[#FFB800]" />
            </div>
            <div>
              <h2
                data-testid="developer-tools-title"
                className="text-3xl font-black tracking-tighter text-white font-['Chivo']"
              >Developer Tools</h2>
              <p
                data-testid="developer-tools-subtitle"
                className="text-sm text-[#8A8A93] mt-1"
              >Developer / Diagnostic Tools — advanced controls not required for normal operation.</p>
            </div>
          </div>
          <div
            data-testid="developer-tools-banner"
            className="flex items-center gap-3 flex-wrap px-3 py-2 rounded-sm border border-[#FFB800]/30 bg-[#FFB800]/5 text-[10px] font-mono uppercase tracking-[0.14em] text-[#FFB800]"
          >
            <AlertTriangle size={11} />
            <span data-testid="developer-tools-banner-no-scan">
              No scanner run by default.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="developer-tools-banner-no-ranking">
              No ranking.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="developer-tools-banner-no-execution">
              No execution.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="developer-tools-banner-no-persistence">
              No persistence.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="developer-tools-banner-human-decides">
              Human decides.
            </span>
          </div>
        </div>

        {/* ── Operator Readiness (Iter98 — moved off Dashboard) ── */}
        <DevSection
          testid="developer-tools-section-operator-readiness"
          title="Operator Readiness"
        >
          <SharedCockpitSafetyStrip />
        </DevSection>

        {/* ── Operator Reference / Protected Wheel Visibility
              (Iter98.1 — moved off Dashboard) ─────────────────── */}
        <DevSection
          testid="developer-tools-section-operator-reference"
          title="Operator Reference / Protected Wheel Visibility"
        >
          <OperatorGuideTile />
          <WheelITMScannerPanel />
        </DevSection>

        {/* ── Controlled Auto-Scans (Iter98 — moved off Dashboard) ── */}
        <DevSection
          testid="developer-tools-section-controlled-auto-scans"
          title="Controlled Auto-Scans"
        >
          <BPCSAutoScanPanel />
          <BCCSAutoScanPanel />
          <IronCondorAutoScanPanel />
          <CFMInventoryContextPanel />
          <CFMSharesAutoScanPanel />
          <CFMLeapsAutoScanPanel />
        </DevSection>

        {/* ── Context Builders ──────────────────────────────── */}
        <DevSection
          testid="developer-tools-section-context-builders"
          title="Context Builders"
        >
          <MarketSnapshotBuilderTile />
          <WESContextBuilderTile />
        </DevSection>

        {/* ── Context Inspection ────────────────────────────── */}
        <DevSection
          testid="developer-tools-section-context-inspection"
          title="Context Inspection"
        >
          <JsonSnapshotDiffHelper />
          <SnapshotReplayViewer />
        </DevSection>

        {/* ── Payload Construction ──────────────────────────── */}
        <DevSection
          testid="developer-tools-section-payload-construction"
          title="Payload Construction"
        >
          <PayloadBuilderTile />
          <MarketScannerBridgeTile />
          <ApplicabilityBoard />
        </DevSection>

        {/* ── Module Preview / Manual Scanners ──────────────── */}
        <DevSection
          testid="developer-tools-section-manual-scanners"
          title="Module Preview / Manual Scanners"
        >
          <ManualToolboxPreviewPanel />
          <BPCSScannerPanel />
          <BCCSScannerPanel />
          <IronCondorScannerPanel />
          <CFMSharesScannerPanel />
          <CFMLeapsScannerPanel />
        </DevSection>

        {/* ── Health / Diagnostics ──────────────────────────── */}
        <DevSection
          testid="developer-tools-section-health"
          title="Health / Diagnostics"
        >
          <ParityHealthTile />
          <ToolboxStatusTile />
          <ScannerCockpitTile />
        </DevSection>
      </div>
  );
}

function DevSection({ testid, title, children }) {
  return (
    <section data-testid={testid} className="mb-8">
      <h3 className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8A8A93] mb-3 pb-1 border-b border-[#26262B]">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
